'use client';

import type React from 'react';
import { createContext, PropsWithChildren, useCallback, useContext, useMemo, useState } from 'react';

import type { MovieDto } from '@cineamo/api/models/movie/v2/MovieDto.types';

import type { UrlQueryParameter } from '@cineamo/api/helpers/url-query-helper';

type ShowtimesFinderContextValue = {
    movieId: MovieDto['id'];
    selectedDate: string;
    setSelectedDate: (date: string) => void;
    apiFilters?: UrlQueryParameter;
    // Theatrical release date of the movie. Required for the PREMIERES view, which
    // lists showings from now up to and including this date. Undefined when unknown.
    releaseDate?: string;
    activeTab?: string;
    setActiveTab: (tab?: string) => void;
    initialTab?: string;
    setInitialTab: (tab?: string) => void;
};

const ShowtimesFinderContext = createContext<ShowtimesFinderContextValue | null>(null);

const ShowtimesFinderProvider: React.FC<
    PropsWithChildren<{
        movieId: MovieDto['id'];
        initialSelectedDate: string;
        apiFilters?: UrlQueryParameter;
        releaseDate?: string;
    }>
> = ({ children, movieId, initialSelectedDate, apiFilters, releaseDate }) => {
    const [selectedDate, setSelectedDate] = useState(initialSelectedDate);
    const [activeTab, setActiveTab] = useState<string | undefined>(undefined);
    const [initialTab, setInitialTabState] = useState<string | undefined>(undefined);

    const setInitialTab = useCallback((tab?: string) => setInitialTabState((current) => current ?? tab), []);

    const value = useMemo(
        () => ({
            movieId,
            selectedDate,
            setSelectedDate,
            apiFilters,
            releaseDate,
            activeTab,
            setActiveTab,
            initialTab,
            setInitialTab
        }),
        [movieId, selectedDate, apiFilters, releaseDate, activeTab, initialTab, setInitialTab]
    );

    return <ShowtimesFinderContext.Provider value={value}>{children}</ShowtimesFinderContext.Provider>;
};

/**
 * Returns the ShowtimesFinder context value, or `null` if no provider is present.
 */
export const useShowtimesFinder = () => useContext(ShowtimesFinderContext);

/**
 * Returns the ShowtimesFinder context value, throwing if no provider is present.
 * Use in components that must be rendered within a `ShowtimesFinderProvider`.
 */
export const useRequiredShowtimesFinder = () => {
    const context = useContext(ShowtimesFinderContext);
    if (!context) {
        throw new Error('useRequiredShowtimesFinder must be used within a ShowtimesFinderProvider');
    }
    return context;
};

export default ShowtimesFinderProvider;
